import { useState } from 'react';
import { Box, Grid, IconButton, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddIcon from '@mui/icons-material/Add';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import AddHighlightDialog from '../components/AddHighlightDialog';

const KeyHighlightsField = ({ field, data, change }) => {
    const highlights = Array.isArray(data[field.key]) ? data[field.key] : [];
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingHighlight, setEditingHighlight] = useState(null);
    const [initialDropFile, setInitialDropFile] = useState(null);

    const updateHighlights = (next) => change(field.key, next);

    const handleSave = (highlight) => {
        const exists = highlights.some((item) => item.id === highlight.id);
        if (exists) {
            const previous = highlights.find((item) => item.id === highlight.id);
            if (
                previous?.image?.startsWith('blob:')
                && previous.image !== highlight.image
            ) {
                URL.revokeObjectURL(previous.image);
            }
            updateHighlights(highlights.map((item) => (item.id === highlight.id ? highlight : item)));
        } else {
            updateHighlights([...highlights, highlight]);
        }
        setEditingHighlight(null);
        setInitialDropFile(null);
    };

    const handleEdit = (highlight) => {
        setEditingHighlight(highlight);
        setInitialDropFile(null);
        setDialogOpen(true);
    };

    const handleDelete = (id) => {
        const target = highlights.find((item) => item.id === id);
        if (target?.image?.startsWith('blob:')) {
            URL.revokeObjectURL(target.image);
        }
        updateHighlights(highlights.filter((item) => item.id !== id));
    };

    const openAddDialog = (file = null) => {
        setEditingHighlight(null);
        setInitialDropFile(file);
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingHighlight(null);
        setInitialDropFile(null);
    };

    return (
        <Box>
            {highlights.length > 0 && (
                <Grid container spacing={2} mb={2}>
                    {highlights.map((item) => (
                        <Grid item xs={12} sm={6} md={4} key={item.id}>
                            <Box
                                sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                }}
                            >
                                {item.image && (
                                    <Box
                                        component="img"
                                        src={item.image}
                                        alt={item.name}
                                        sx={{ width: '100%', height: 120, objectFit: 'cover', display: 'block' }}
                                    />
                                )}
                                <MDBox p={1.5}>
                                    <MDTypography variant="subtitle2" fontWeight="medium">
                                        {item.name}
                                    </MDTypography>
                                    <MDTypography variant="caption" color="text">
                                        {item.location}
                                    </MDTypography>
                                    <MDBox display="flex" justifyContent="flex-end" gap={0.5} mt={1}>
                                        <Tooltip title="Edit highlight">
                                            <IconButton size="small" onClick={() => handleEdit(item)}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove highlight">
                                            <IconButton size="small" onClick={() => handleDelete(item.id)}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </MDBox>
                                </MDBox>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            )}

            <MDBox mt={highlights.length ? 1.5 : 0}>
                <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => openAddDialog()}
                >
                    Add Highlight
                </MDButton>
            </MDBox>

            <AddHighlightDialog
                open={dialogOpen}
                onClose={closeDialog}
                onSave={handleSave}
                initialHighlight={editingHighlight}
                initialFile={initialDropFile}
            />
        </Box>
    );
};

export default KeyHighlightsField;
